import get from 'lodash/get';
import { Redirect, Route } from 'react-router-dom';
import { USER_ROLES } from '../constants';
import { RouterContext } from '../contexts';
import { hasPermission } from 'src/_shared/components/permission-gate/pathHelpers.js';

export function AuthenticatedRoute(props) {
	const {
		component: Component,
		roles = [],
		sidebarOpen = true,
		path = '/',
	} = props;

	const rest = { ...props };
	delete rest.component;
	return (
		<RouterContext.Consumer>
			{({ auth, currentUser }) => {
				const role = currentUser?.role;
				let scopedPolicy = currentUser?.scopedPolicy || {};
				if (typeof scopedPolicy === 'string') {
					try {
						scopedPolicy = JSON.parse(scopedPolicy);
					} catch (e) {
						scopedPolicy = {};
						console.error(e);
					}
				}
				const platformConfig =
					typeof currentUser?.company?.platformConfig === 'string'
						? JSON.parse(currentUser.company.platformConfig)
						: {
								primaryPlatform: 'referrals',
								filterMode: 'exclusionary',
								filterList: [],
							};
				const checkFiltered = (filteredPage) => {
					if (
						currentUser?.role === USER_ROLES.SUPER_ADMIN ||
						currentUser?.isScopedUser
					)
						return false;
					return platformConfig?.filterList?.some(
						(value) => value === filteredPage
					);
				};

				const recHubDisabled =
					currentUser?.company?.recognitionSettings?.disableRecHub;
				const redirectsArray = [
					{ filteredPage: 'points', paths: ['/giftcardstore', '/points'] },
					{
						filteredPage: 'internal',
						paths: [
							'/myapplications',
							'/career',
							'/internal',
							'/internal-applicants',
							'/open',
							'/openDetails',
						],
					},
					{ filteredPage: 'jobs', paths: ['/browsejobs', '/jobs'] },
					{
						filteredPage: 'referrals',
						paths: [
							'/my-referrals',
							'/referrals',
							'/myreferrals',
							'/mycontacts',
							'/deck',
						],
					},
					{ filteredPage: 'employees', paths: ['/employees', '/editprofile'] },
					{
						filteredPage: 'settings',
						paths: [
							'/settings',
							'/form-builder',
							'/job-notification-email',
							'/branding',
							'/employee-dashboard-settings',
						],
					},
					{
						filteredPage: 'bonuses',
						paths: ['/builder', '/bonuses', '/bonus-campaigns', '/mybonuses'],
					},
					{ filteredPage: 'messages', paths: ['/messagecenter'] },
					{ filteredPage: 'reports', paths: ['/report-builder'] },
					{ filteredPage: 'customPages', paths: ['/custompage'] },
					{ filteredPage: 'announcements', paths: ['/announcements'] },
					{ filteredPage: 'recognitionsCatalog', paths: ['/recognitions'] },
					{
						filteredPage: 'dashboard',
						paths: ['/dashboard'],
						redirectTo:
							currentUser?.role === USER_ROLES.ADMIN ||
							currentUser?.role === USER_ROLES.SUPER_ADMIN
								? '/recognition-approvals'
								: recHubDisabled
									? '/my-recognitions'
									: '/recognition-hub',
					},
				];
				const redirectTo = currentUser?.role ? '/dashboard' : '/';
				return (
					<Route
						{...rest}
						render={(props) => {
							// Full requested URL (path + query + hash) so deep links keep
							// their parameters through the login round trip. (ERIN-8413)
							const requestedURL = `${props.location.pathname}${props.location.search}${props.location.hash}`;

							if (!auth) {
								return (
									<Redirect
										to={{
											pathname: '/login',
											state: {
												currentUser,
												redirectURL: requestedURL,
											},
										}}
									/>
								);
							}

							if (!currentUser?.id) {
								return (
									<Redirect
										to={{
											pathname: '/logout',
											state: {
												redirectURL: requestedURL,
											},
										}}
									/>
								);
							}

							// These redirects are for the filterList that was set from Super Admin Console
							if (
								platformConfig.filterList.length > 0 &&
								currentUser.role !== USER_ROLES.SUPER_ADMIN &&
								!currentUser.isScopedUser
							) {
								for (const {
									filteredPage,
									paths,
									redirectTo,
								} of redirectsArray) {
									if (
										checkFiltered(filteredPage) &&
										paths.includes(props.location.pathname)
									) {
										return <Redirect to={redirectTo || '/'} />;
									}
								}
							}

							if (
								(role === USER_ROLES.SUPER_ADMIN ||
									role === USER_ROLES.SUPPORT_ADMIN) &&
								props.location.pathname === '/dashboard'
							) {
								return <Redirect to="/admin-console" />;
							}

							const supportAdmin =
								typeof currentUser?.admin === 'string'
									? JSON.parse(currentUser?.admin)
									: null;
							const supportAdminPermissions = supportAdmin?.permissions === '*';
							if (
								role !== USER_ROLES.SUPER_ADMIN &&
								role !== USER_ROLES.SUPPORT_ADMIN &&
								!supportAdminPermissions &&
								props.location.pathname === '/admin-console'
							) {
								return <Redirect to="/" />;
							}

							// Integrations console is service-admin only: requires full
							// admin permissions ('*'), regardless of role.
							if (
								!supportAdminPermissions &&
								props.location.pathname === '/admin-integrations'
							) {
								return <Redirect to="/" />;
							}

							// If corresponding manager permission type is set to "hidden"
							// and the user's role is manager, navigate to the app root
							if (role === USER_ROLES.MANAGER) {
								// Defines advanced manager permissions, if they've never been created
								// it assumes the default values.
								const disableManagerPermissionsByType =
									typeof currentUser?.company
										?.disableManagerPermissionsByType === 'string'
										? JSON.parse(
												currentUser.company.disableManagerPermissionsByType
											)
										: {
												managerReferrals: 'edit',
												managerJobs: 'edit',
												managerBonuses: 'hidden',
												managerEmployees: 'hidden',
											};
								if (
									(disableManagerPermissionsByType.managerBonuses ===
										'hidden' &&
										props.location.pathname === '/bonuses') ||
									(disableManagerPermissionsByType.managerEmployees ===
										'hidden' &&
										props.location.pathname.startsWith('/employees')) ||
									(disableManagerPermissionsByType.managerJobs === 'hidden' &&
										props.location.pathname.startsWith('/jobs')) ||
									(disableManagerPermissionsByType.managerReferrals ===
										'hidden' &&
										props.location.pathname.startsWith('/referrals'))
								) {
									return <Redirect to={redirectTo} />;
								}
							}

							// If roles are specified and the current user's role isn't
							// included then navigate to the app root
							// NOTE: assumes no specified roles means any authenticated user
							// can access the route
							if (
								roles.length > 0 &&
								(!roles.includes(role) || currentUser?.isScopedUser)
							) {
								let canAccess = false;
								if (
									currentUser?.isScopedUser &&
									scopedPolicy?.paths?.length > 0
								) {
									canAccess = hasPermission(path, scopedPolicy.paths);
								}
								if (!canAccess) return <Redirect to={redirectTo} />;
							}

							return (
								<Component
									{...props}
									googleAuthClientID={get(rest, 'secrets.GoogleAuthClientID')}
									sidebarOpen={sidebarOpen}
								/>
							);
						}}
					/>
				);
			}}
		</RouterContext.Consumer>
	);
}
